import {of as observableOf, forkJoin as observableForkJoin, Observable} from 'rxjs';

import {catchError, map} from 'rxjs/operators';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {DetailControl} from './detail-tabconfig';
import {AbstractControlComponent} from './controls/abstract-control';
import {AddViewEvent} from '../events/addviewevent';
import {UpdateResourceEvent} from '../events/updateresourceevent';
import {UpdateLocalizedValueEvent} from '../events/updatelocalizedvalueevent';
import {UpdateLocaleEvent} from '../events/updatelocaleevent';
import {AbstractControlReadonlyComponent} from './controls/abstract-control-readonly';
import {ResourceWithPermissionHints} from '../resource';
import {CacheService} from '../../cache/cache.service';
import {UIConfiguration} from '../../cache/uiconfiguration/uiconfiguration';

@Component({
  selector: 'viewtab',
  templateUrl: './viewtab.component.html',
  styleUrls: ['./viewtab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ViewTabComponent implements AfterViewInit {
  _controls: DetailControl[];
  private _objectType: string;
  private _editEnabled: boolean;
  private _freshResource: boolean;
  @Input() objectID: string;
  @Input() objectDisplayName: string;
  @Input() resource: ResourceWithPermissionHints;

  _errors: string[] = [];
  showErrorsDetail: boolean = false;

  @ViewChildren('control') controlComponents: QueryList<AbstractControlComponent>;
  @ViewChildren('roControl') readonlyControlComponents: QueryList<AbstractControlReadonlyComponent>;

  ngAfterViewInit(): void {
    this.editEnabled = this._editEnabled;
  }

  @Input()
  set controls(controls: DetailControl[]) {
    this._controls = controls;
  }

  @Input()
  set objectType(objectType: string) {
    this._objectType = objectType;
  }

  @Input()
  set editEnabled(editEnabled: boolean) {
    this._editEnabled = editEnabled;
  }

  @Input()
  set freshResource(freshResource: boolean) {
    this._freshResource = freshResource;
  }

  @Output() onUpdateResource: EventEmitter<UpdateResourceEvent> = new EventEmitter();
  @Output() onUpdateLocalizedValue: EventEmitter<UpdateLocalizedValueEvent> = new EventEmitter();
  @Output() onUpdateLocale: EventEmitter<UpdateLocaleEvent> = new EventEmitter();
  @Output() onAddView: EventEmitter<AddViewEvent> = new EventEmitter<AddViewEvent>();
  @Output() onUpdateValidity: EventEmitter<any> = new EventEmitter<any>();

  get editEnabled(): boolean {
    return this._editEnabled;
  }

  get freshResource(): boolean {
    return this._freshResource;
  }

  get objectType(): string {
    return this._objectType;
  }

  constructor(
    public changeDetector: ChangeDetectorRef,
    private cacheService: CacheService,
  ) {}

  get showDescription(): Observable<boolean> {
    return this.cacheService
      .getUIConfiguration()
      .pipe(map((uiConfig: UIConfiguration) => uiConfig.usedescription));
  }
  get showHint(): Observable<boolean> {
    return this.cacheService
      .getUIConfiguration()
      .pipe(map((uiConfig: UIConfiguration) => uiConfig.usehint));
  }

  updateResource(name: string, value: any) {
    this.onUpdateResource.emit(new UpdateResourceEvent(name, value));
  }

  updateLocalizedValue(name: string, value: any) {
    this.onUpdateLocalizedValue.emit(new UpdateLocalizedValueEvent(name, value));
  }

  updateLocale(value: any) {
    this.onUpdateLocale.emit(new UpdateLocaleEvent(value));
  }

  updateValidity(value: any) {
    this.onUpdateValidity.emit(value);
  }

  getErrorCount(includeNonTouched: boolean = false): Observable<number> {
    if (this.controlComponents != null) {
      const validationObs: Observable<boolean>[] = [];
      this.controlComponents.forEach((item, index) => {
        validationObs[index] = item.revalidate(includeNonTouched);
      });

      if (validationObs.length > 0) {
        // If there are tabs with no controls including validation (labels) this if is necessary
        return observableForkJoin(validationObs).pipe(
          map((valids: boolean[]) => {
            let errors: number = 0;
            valids.forEach((valid: boolean) => {
              if (valid !== true) {
                errors++;
              }
            });
            return errors;
          }),
        );
      } else {
        return observableOf(0);
      }
    } else {
      return observableOf(0);
    }
  }

  markAllControlsAsNotTouched() {
    if (this.controlComponents == null) return;
    this.controlComponents.forEach((control: AbstractControlComponent) => {
      control.formControl.markAsUntouched();
    });
  }

  addControlError(control: string, error: string): void {
    this._errors.push(control + ': ' + error);
    setTimeout(() => {
      this.changeDetector.markForCheck();
    }, 1000);
  }

  ignoreCatchObservable(obs: Observable<any>): Observable<any> {
    if (obs == null) return null;
    return obs.pipe(
      catchError(error => {
        return observableOf(null);
      }),
    );
  }

  refreshUI() {
    this.controlComponents.forEach((control: AbstractControlComponent) => {
      control.refreshUI();
    });
  }

  saveAllControlValues(): void {
    if (this.controlComponents == null) return;
    this.controlComponents.forEach((control: AbstractControlComponent) => {
      control.formControl.updateValueAndValidity({emitEvent: true});
    });
  }

  resetUI(): void {
    if (
      this.controlComponents === null ||
      this.controlComponents === undefined ||
      Array.isArray(this.controlComponents)
    )
      return;
    this.controlComponents.forEach((control: AbstractControlComponent) => {
      control.resetUI();
    });
  }
}
